import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Lightbulb, Sparkles, RefreshCw, ChevronDown, Clock } from 'lucide-react';
import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'mix', label: 'Mix of Everything', description: 'Variety of topics' },
  { value: 'trending', label: 'Trending Topics', description: "What's hot right now" },
  { value: 'fun_facts', label: 'Fun Facts', description: 'Did you know...' },
  { value: 'local', label: 'Local Norfolk', description: "King's Lynn & area" },
  { value: 'debate', label: 'Phone-In Debates', description: 'Get listeners calling' },
  { value: 'history', label: 'On This Day', description: 'Historical moments' },
];

const showTypes = [
  { value: 'breakfast', label: 'Breakfast Show', time: '6am - 10am', icon: '06' },
  { value: 'mid_morning', label: 'Mid-Morning', time: '10am - 1pm', icon: '10' },
  { value: 'afternoon', label: 'Afternoon', time: '1pm - 5pm', icon: '13' },
  { value: 'evening', label: 'Evening', time: '5pm - 10pm', icon: '17' },
  { value: 'daytime', label: 'General', time: 'Any time', icon: '12' },
];

const getAutoShowType = () => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 10) return 'breakfast';
  if (hour >= 10 && hour < 13) return 'mid_morning';
  if (hour >= 13 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'daytime';
};

const TalkIdeasSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('mix');
  const [showType, setShowType] = useState(getAutoShowType);
  const [ideas, setIdeas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [showDropdownOpen, setShowDropdownOpen] = useState(false);

  const generateIdeas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/talk-ideas/generate`, null, {
        params: { category: selectedCategory, show_type: showType }
      });
      setIdeas(response.data);
      toast.success('Ideas generated!');
    } catch (error) {
      console.error('Error generating ideas:', error);
      toast.error('Failed to generate ideas');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showType]);

  const formatIdeasContent = useCallback((content) => {
    if (!content) return '';
    const formatted = content
      .replace(/(\d+\.\s)/g, '<br/><strong class="idea-number">$1</strong>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return DOMPurify.sanitize(formatted, { ALLOWED_TAGS: ['br', 'strong'], ALLOWED_ATTR: ['class'] });
  }, []);

  const selectedCategoryData = useMemo(() => categories.find(c => c.value === selectedCategory), [selectedCategory]);
  const selectedShowData = useMemo(() => showTypes.find(s => s.value === showType), [showType]);

  return (
    <div className="content-card content-card-large" data-testid="talk-ideas-section">
      <div className="card-header">
        <h2>
          <Lightbulb size={20} className="inline" style={{ color: '#ffd700' }} /> AI Talk Ideas
        </h2>
      </div>

      <ScrollArea className="card-content">
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '1rem' }}>
            AI-generated talk ideas tailored to your show slot
          </p>

          {/* Show Type Selector */}
          <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
            <button
              onClick={() => { setShowDropdownOpen(!showDropdownOpen); setCatDropdownOpen(false); }}
              className="talk-dropdown-btn"
              data-testid="show-type-dropdown"
            >
              <span>
                <Clock size={16} style={{ marginRight: '6px', verticalAlign: 'middle', color: '#ffd700' }} />
                <strong>{selectedShowData?.label}</strong>
                <span className="talk-dropdown-hint">{selectedShowData?.time}</span>
              </span>
              <ChevronDown size={18} style={{ transform: showDropdownOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
            </button>

            {showDropdownOpen && (
              <div className="talk-dropdown-menu" style={{ zIndex: 101 }}>
                {showTypes.map((st) => (
                  <button
                    key={st.value}
                    onClick={() => { setShowType(st.value); setShowDropdownOpen(false); }}
                    className={`talk-dropdown-item ${showType === st.value ? 'active' : ''}`}
                    data-testid={`show-type-${st.value}`}
                  >
                    <span>{st.label}</span>
                    <span className="talk-dropdown-hint">{st.time}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Category Dropdown */}
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <button
              onClick={() => { setCatDropdownOpen(!catDropdownOpen); setShowDropdownOpen(false); }}
              className="talk-dropdown-btn"
              data-testid="category-dropdown"
            >
              <span>
                <strong>{selectedCategoryData?.label}</strong>
                <span className="talk-dropdown-hint">{selectedCategoryData?.description}</span>
              </span>
              <ChevronDown size={18} style={{ transform: catDropdownOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
            </button>

            {catDropdownOpen && (
              <div className="talk-dropdown-menu" style={{ zIndex: 100 }}>
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => { setSelectedCategory(cat.value); setCatDropdownOpen(false); }}
                    className={`talk-dropdown-item ${selectedCategory === cat.value ? 'active' : ''}`}
                    data-testid={`category-${cat.value}`}
                  >
                    <span>{cat.label}</span>
                    <span className="talk-dropdown-hint">{cat.description}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Generate Button */}
          <Button
            onClick={generateIdeas}
            disabled={loading}
            className="generate-btn"
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)',
              color: '#000',
              fontWeight: 600,
              fontSize: '1rem'
            }}
            data-testid="generate-ideas-btn"
          >
            {loading ? (
              <>
                <RefreshCw size={18} className="spinning" style={{ marginRight: '0.5rem' }} />
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={18} style={{ marginRight: '0.5rem' }} />
                Generate Ideas
              </>
            )}
          </Button>
        </div>

        {/* Generated Ideas Output */}
        {ideas && (
          <div className="ideas-output" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            padding: '1.25rem',
            border: '1px solid rgba(255, 215, 0, 0.2)'
          }}>
            <div style={{ 
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '1rem', paddingBottom: '0.75rem',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              <span style={{ color: '#ffd700', fontWeight: 600 }}>
                {categories.find(c => c.value === ideas.category)?.label || 'Ideas'}
              </span>
              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                {new Date(ideas.generated_at).toLocaleTimeString()}
              </span>
            </div>
            
            <div 
              style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '0.95rem' }}
              dangerouslySetInnerHTML={{ __html: formatIdeasContent(ideas.ideas) }}
            />
          </div>
        )}

        {!ideas && !loading && (
          <div style={{ 
            textAlign: 'center', padding: '2rem', color: '#666',
            background: 'rgba(255,255,255,0.02)', borderRadius: '12px',
            border: '2px dashed rgba(255,215,0,0.2)'
          }}>
            <Lightbulb size={40} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p>Select your show slot and category, then click Generate</p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default TalkIdeasSection;
